import { Link } from "react-router-dom";
import {
  ArrowRight, ArrowLeft, ShieldCheck, Search, Gavel, Layers, FileSearch,
  Microscope, ChevronRight,
} from "lucide-react";
import PublicLayout from "@/components/PublicLayout";
import usePageMeta from "@/hooks/usePageMeta";

const IP_SERVICES = [
  {
    slug: "patentability-novelty-search",
    icon: Search,
    title: "Patentability / Novelty Search",
    pitch: "Establish whether your invention is patentable — before you file.",
    deliverables: [
      "Prior-art landscape across patent and non-patent literature",
      "Novelty assessment with cited references and claim mapping",
      "Patentability recommendation with confidence rating",
    ],
    decision: "Decide whether to file, refine, or pivot before incurring drafting cost.",
  },
  {
    slug: "invalidation-prior-art-search",
    icon: Gavel,
    title: "Invalidation / Prior Art Search",
    pitch: "Find the references that break an asserted patent.",
    deliverables: [
      "Exhaustive prior-art identification (patent + scientific literature)",
      "Claim-by-claim invalidity contentions",
      "Strategic narrative for opposition, IPR, or litigation",
    ],
    decision: "Equip counsel with the evidence to invalidate or design around.",
  },
  {
    slug: "freedom-to-operate",
    icon: ShieldCheck,
    title: "Freedom to Operate (FTO) Analysis",
    pitch: "Launch with confidence — and a defensible record of due diligence.",
    deliverables: [
      "Jurisdiction-by-jurisdiction patent risk mapping",
      "Claim-construction analysis on third-party patents",
      "Design-around recommendations and risk-tier ranking",
    ],
    decision: "Approve commercial launch with documented IP risk posture.",
  },
  {
    slug: "patent-portfolio-analysis",
    icon: Layers,
    title: "Patent Portfolio Analysis & Management",
    pitch: "Optimise the portfolio you have. Plan the one you need.",
    deliverables: [
      "Portfolio health assessment (depth, geographic spread, expiry profile)",
      "White-space and gap analysis against competitor portfolios",
      "Renewal, divestment, and filing strategy recommendations",
    ],
    decision: "Allocate IP budget to the assets that protect the business model.",
  },
  {
    slug: "patent-infringement-eou",
    icon: FileSearch,
    title: "Patent Infringement & Evidence of Use Analysis",
    pitch: "Build the technical record before the legal record.",
    deliverables: [
      "Claim-chart evidence-of-use packages",
      "Reverse-engineering and product-teardown analysis where relevant",
      "Damages-foundation technical narrative for counsel",
    ],
    decision: "Convert a suspicion into a defensible infringement assertion.",
  },
];

export default function IPServicesPage() {
  usePageMeta({
    title: "IP Services — Patent Intelligence & Strategy · Luminary",
    description: "Patentability, invalidation, freedom-to-operate, portfolio analysis, and infringement / evidence-of-use. Forensic-grade IP intelligence aligned to commercial decisions.",
    path: "/services/ip",
    keywords: "patent search, freedom to operate, FTO, patent portfolio, IP advisory, invalidation search, EoU analysis",
  });

  return (
    <PublicLayout>
      {/* HERO */}
      <section className="bg-white border-b border-[var(--border)] grain" data-testid="ip-services-page">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <Link to="/" className="btn-ghost mb-8 inline-flex" data-testid="ip-back">
            <ArrowLeft size={12} /> Home
          </Link>
          <div className="eyebrow">IP Services</div>
          <h1 className="font-display font-black text-[clamp(2.4rem,6vw,5rem)] mt-4 leading-[0.95] tracking-tight text-[var(--navy)]">
            Patent intelligence,<br />
            <span className="italic font-medium text-[var(--gold)]">aligned to your commercial bet.</span>
          </h1>
          <p className="mt-8 font-display italic text-2xl text-[var(--slate)] max-w-3xl leading-snug">
            Forensic-grade IP search, FTO, portfolio strategy, and evidence-of-use analysis —
            delivered to support commercial decisions, not just legal opinions.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/strategy-session" className="btn-primary" data-testid="ip-cta-schedule">
              Schedule a scoping call <ArrowRight size={14} />
            </Link>
            <Link to="/methodology" className="btn-secondary" data-testid="ip-cta-methodology">
              See our methodology
            </Link>
          </div>
        </div>
      </section>

      {/* SERVICES GRID */}
      <section className="border-b border-[var(--border)] bg-[var(--bg-2)] py-16 sm:py-20">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-12 gap-10 mb-12">
            <div className="lg:col-span-6">
              <div className="eyebrow">Service Catalog</div>
              <h2 className="font-display font-black text-3xl sm:text-4xl mt-3 leading-tight text-[var(--navy)]">
                Five services. One commercial-decision lens.
              </h2>
            </div>
            <div className="lg:col-span-6">
              <p className="text-[15px] text-[var(--slate)] leading-relaxed">
                Every IP engagement at Luminary is scoped to a commercial decision — file or pivot,
                launch or design around, defend or assert. The deliverable is sourced, claim-mapped,
                and editorially reviewed before it leaves the desk.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--border)] border border-[var(--border)]">
            {IP_SERVICES.map((s) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.slug}
                  className="bg-white p-6 sm:p-7 flex flex-col hover-lift"
                  data-testid={`ip-service-${s.slug}`}
                >
                  <div className="w-11 h-11 bg-[var(--navy)] text-[var(--gold)] flex items-center justify-center">
                    <Icon size={18} />
                  </div>
                  <h3 className="font-display font-bold text-xl mt-5 text-[var(--navy)] leading-tight">
                    {s.title}
                  </h3>
                  <p className="mt-2 text-sm text-[var(--slate)] leading-relaxed">{s.pitch}</p>
                  <div className="mt-5 pt-4 border-t border-[var(--border)]">
                    <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--gold)]">
                      What you get
                    </div>
                    <ul className="mt-2.5 space-y-1.5 text-[13px] text-[var(--navy)]">
                      {s.deliverables.map((d, i) => (
                        <li key={i} className="border-l-2 border-[var(--gold)] pl-3 leading-snug">
                          {d}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="mt-5 pt-4 border-t border-[var(--border)] text-[12px] text-[var(--slate)] italic leading-snug">
                    {s.decision}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* APPROACH */}
      <section className="border-b border-[var(--border)] bg-white py-16 sm:py-20">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <div className="eyebrow">Approach</div>
              <h2 className="font-display font-black text-3xl sm:text-4xl mt-3 leading-tight text-[var(--navy)]">
                Engineered for defensibility.
              </h2>
              <p className="mt-5 text-[15px] text-[var(--slate)] leading-relaxed">
                Our IP engagements run on the same five-stage pipeline as the rest of Luminary —
                collection, validation, analysis, strategic assessment, recommendations — adapted to
                patent search and analysis discipline.
              </p>
              <Link to="/methodology" className="mt-7 btn-secondary inline-flex">
                <Microscope size={13} /> Read the methodology
              </Link>
            </div>
            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-px bg-[var(--border)] border border-[var(--border)] self-start">
              {[
                ["Forensic search", "Patent and non-patent literature, multilingual sources, registry-level data."],
                ["Claim-mapped output", "Every reference mapped against claim elements — no narrative without evidence."],
                ["Editorial review", "Every deliverable signed off by a senior IP analyst before release."],
                ["Decision linkage", "Every output ends with a commercial recommendation — not just a legal opinion."],
              ].map(([t, d]) => (
                <div key={t} className="bg-white p-5">
                  <h3 className="font-display font-bold text-base text-[var(--navy)] leading-tight">{t}</h3>
                  <p className="mt-2 text-[13px] text-[var(--slate)] leading-relaxed">{d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[var(--navy)] text-white py-16 sm:py-20">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 text-center">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--gold)]">Engage</div>
          <h2 className="font-display font-black text-3xl sm:text-5xl mt-3 leading-tight">
            Bring us the IP question.
          </h2>
          <p className="mt-5 text-white/75 max-w-2xl mx-auto leading-relaxed">
            A scoping call with a Luminary IP analyst returns two outputs within one business day:
            a defined work plan and a fixed-fee proposal.
          </p>
          <div className="mt-9 flex flex-wrap justify-center gap-3">
            <Link to="/strategy-session" className="btn-gold" data-testid="ip-bottom-cta">
              Schedule scoping call <ArrowRight size={14} />
            </Link>
            <Link to="/contact" className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--gold)] hover:text-white self-center inline-flex items-center gap-2">
              Or contact the desk <ChevronRight size={12} />
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
